import Phaser from 'phaser'
import { hasMsg, t, type JKGameMsgKey } from '../i18n'
import { ZONES, type ZoneThemeId } from '../sim/JKGame_content'
import type { Archetype } from '../sim/JKGame_types'

// Localized zone strings (names/desc/lore live in i18n under `zone.<id>.*`). All current
// zones are fully keyed, but the keys are content-driven, so degrade gracefully (parity
// with weaponName/itemName/seasonName) — a future zone added without i18n shows a generic
// name and blank flavour rather than a raw key, honouring the no-raw-key rule.
export const zoneName = (i: number): string => {
  const k = `zone.${ZONES[i].id}.name`
  return hasMsg(k) ? t(k as JKGameMsgKey) : t('zone.unknown')
}
export const zoneDesc = (i: number): string => {
  const k = `zone.${ZONES[i].id}.desc`
  return hasMsg(k) ? t(k as JKGameMsgKey) : ''
}
export const zoneLore = (i: number): string => {
  const k = `zone.${ZONES[i].id}.lore`
  return hasMsg(k) ? t(k as JKGameMsgKey) : ''
}

// Tint applied to the (blonde) character sprite to identify a bot's archetype
// on the map. Player is untinted.
export const ARCH_TINT: Record<Archetype, number> = {
  player: 0xffffff,
  hunter: 0xff8a8a,
  survivor: 0xc7cfdb,
  looter: 0x7fe6d2,
  medic: 0x9fe8af,
  veteran: 0xcbb0ff,
  boss: 0xffdf8a,
}

// Archetype → display-label i18n key. Typed Record so TS forces EVERY Archetype to have a
// label — a new archetype can't compile without one. Replaces a dynamic `arch.${x}` template
// cast in CombatScene that, for an unexpected value, would have leaked the raw key to the
// player (parity with ARCH_TINT above and the controller's FAIL_KEYS).
export const ARCH_LABEL: Record<Archetype, JKGameMsgKey> = {
  player: 'arch.player',
  hunter: 'arch.hunter',
  survivor: 'arch.survivor',
  looter: 'arch.looter',
  medic: 'arch.medic',
  veteran: 'arch.veteran',
  boss: 'arch.boss',
}

// Real art from /ASSETS: "The Fan-tasy Tileset" (top-down RPG tiles/objects +
// a 40×48 4-direction character) and "heroines_all" (13 illustrated portrait
// sets × 6 emotions). Loaded once in JKGame_PreloadScene. Texture KEYS are the
// stable contract the scenes reference.

export const TILES = ['grass', 'dirt', 'water'] as const
export const URBAN_TILES = ['dirt', 'concrete', 'paving'] as const
export const URBAN_PROPS = ['barricade', 'barrel-red', 'tire', 'bench', 'trashcan', 'crate'] as const

export const OBJECTS = [
  'tree1', 'tree2', 'tree3', 'tree4', 'bush1', 'bush2',
  'rock1', 'rock2', 'rock3',
  'house1', 'house2', 'house3', 'house4', 'well', 'gate',
  'crate', 'barrel', 'sign', 'lamp', 'bench', 'hay', 'fire', 'plant', 'stump',
] as const

export const PORTRAIT_SETS: readonly string[] = Array.from({ length: 13 }, (_v, i) => `fem_${i + 1}`)
export const PORTRAIT_EMOTIONS = ['plane', 'angry', 'surprised', 'sad', 'smile', 'blush'] as const
export type Emotion = (typeof PORTRAIT_EMOTIONS)[number]

export const CHAR_FRAME = { width: 40, height: 48 }

export function loadAssets(scene: Phaser.Scene): void {
  for (const t of TILES) scene.load.image(`tile_${t}`, `art/tiles/${t}.png`)
  for (const t of URBAN_TILES) scene.load.image(`urban_${t}`, `art/urban/${t}.png`)
  for (const p of URBAN_PROPS) scene.load.image(`urban_prop_${p}`, `art/urban/props/${p}.png`)
  for (const o of OBJECTS) scene.load.image(`obj_${o}`, `art/obj/${o}.png`)
  scene.load.spritesheet('char_idle', 'art/char/idle.png', { frameWidth: CHAR_FRAME.width, frameHeight: CHAR_FRAME.height })
  scene.load.spritesheet('char_walk', 'art/char/walk.png', { frameWidth: CHAR_FRAME.width, frameHeight: CHAR_FRAME.height })
  for (const s of PORTRAIT_SETS) for (const e of PORTRAIT_EMOTIONS) scene.load.image(`por_${s}_${e}`, `art/portraits/${s}/${e}.png`)
}

export const objKey = (name: string): string => `obj_${name}`
export const porKey = (set: string, emo: Emotion): string => `por_${set}_${emo}`
export const urbanTileKey = (name: (typeof URBAN_TILES)[number]): string => `urban_${name}`
export const urbanPropKey = (name: (typeof URBAN_PROPS)[number]): string => `urban_prop_${name}`

/** Deterministic portrait set per entity id, so each bot keeps the same face. */
export function portraitSetFor(id: string): string {
  let h = 0
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0
  return PORTRAIT_SETS[h % PORTRAIT_SETS.length]
}

export interface ZoneTheme {
  center: string // objKey for the room's centrepiece
  border: 'tree' | 'rock' | 'bush' | 'dense'
}

export function zoneTheme(theme: ZoneThemeId): ZoneTheme {
  switch (theme) {
    case 'lighthouse':
      return { center: objKey('lamp'), border: 'rock' }
    case 'cliff':
      return { center: objKey('rock2'), border: 'rock' }
    case 'shrine':
      return { center: objKey('gate'), border: 'tree' }
    case 'rock':
      return { center: objKey('rock1'), border: 'rock' }
    case 'forest':
      return { center: objKey('tree1'), border: 'dense' }
    case 'port':
      return { center: objKey('barrel'), border: 'rock' }
    case 'beach':
      return { center: objKey('bench'), border: 'rock' }
    case 'water':
      return { center: objKey('well'), border: 'bush' }
    case 'building':
      return { center: objKey('house1'), border: 'bush' }
  }
}

export function zoneFloorTile(theme: ZoneThemeId): string {
  switch (theme) {
    case 'building':
      return urbanTileKey('paving')
    case 'port':
    case 'lighthouse':
      return urbanTileKey('concrete')
    case 'water':
      return 'tile_water'
    case 'beach':
    case 'rock':
    case 'cliff':
      return urbanTileKey('dirt')
    case 'forest':
    case 'shrine':
      return 'tile_grass'
  }
}

/** Purchased Urban Pack props for built zones. Empty themes stay visually quiet and readable. */
export function zoneUrbanProps(theme: ZoneThemeId): string[] {
  switch (theme) {
    case 'building':
      return [urbanPropKey('bench'), urbanPropKey('trashcan')]
    case 'port':
      return [urbanPropKey('barrel-red'), urbanPropKey('crate')]
    case 'lighthouse':
      return [urbanPropKey('barricade'), urbanPropKey('tire')]
    default:
      return []
  }
}

const BORDER_SETS: Record<ZoneTheme['border'], string[]> = {
  tree: ['tree1', 'tree2', 'tree3', 'tree4'],
  rock: ['rock1', 'rock2', 'rock3'],
  bush: ['bush1', 'bush2'],
  dense: ['tree1', 'tree2', 'tree3', 'tree4', 'bush1', 'bush2'],
}

/** A random border object key for the given border kind (uses Math.random). */
export function borderObj(kind: ZoneTheme['border']): string {
  const set = BORDER_SETS[kind]
  return objKey(set[Math.floor(Math.random() * set.length)])
}
